import { useCallback, useEffect, useRef } from 'react';

interface UseInfiniteScrollProps {
	hasNextPage: boolean;
	isLoading: boolean;
	isFetchingNextPage: boolean;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	fetchNextPage: () => Promise<any>;
	bufferPx?: number;
}

export const useInfiniteScroll = ({
	hasNextPage,
	isLoading,
	isFetchingNextPage,
	fetchNextPage,
	bufferPx = 500,
}: UseInfiniteScrollProps) => {
	const isFetching = useRef(false);
	const loadMoreRef = useRef<HTMLDivElement>(null);

	const handleIntersection = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			const target = entries[0];

			if (
				target?.isIntersecting &&
				hasNextPage &&
				!isLoading &&
				!isFetchingNextPage &&
				!isFetching.current
			) {
				isFetching.current = true;
				fetchNextPage().finally(() => {
					isFetching.current = false;
				});
			}
		},
		[hasNextPage, isLoading, isFetchingNextPage, fetchNextPage],
	);

	useEffect(() => {
		const options = {
			root: null,
			rootMargin: `0px 0px ${bufferPx}px 0px`,
			threshold: 0.1,
		};

		const observer = new IntersectionObserver(handleIntersection, options);

		if (loadMoreRef.current) {
			observer.observe(loadMoreRef.current);
		}

		return () => {
			observer.disconnect();
		};
	}, [handleIntersection, bufferPx]);

	return {
		loadMoreRef,
		isLoading: isLoading || isFetchingNextPage,
		hasMore: hasNextPage,
	};
};
